import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { ApplicationForm, type ApplicationData } from '../components/careers/ApplicationForm';
import { Footer } from '../components/Footer';
import Notification from '../components/ui/Notification';
import { jobs } from '../data/jobs';

export const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'warning';
    title: string;
    message: string;
  }>({
    isVisible: false,
    type: 'success',
    title: '',
    message: ''
  });

  const job = jobs.find(j => j.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!job) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Job Not Found</h2>
          <button
            onClick={() => navigate('/careers')}
            className="text-blue-600 hover:text-blue-800"
          >
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  const handleApplicationSubmit = async (data: ApplicationData) => {
    // Handle form submission
    console.log('Application submitted:', data);

    if (data.success) {
      // Show success notification
      setNotification({
        isVisible: true,
        type: 'success',
        title: 'Application Submitted Successfully!',
        message: `Thank you for your application to ${job.title}. We'll review your application and get back to you soon.`
      });

      // Navigate back to careers page after a delay
      setTimeout(() => {
        navigate('/careers');
      }, 3000);
    } else {
      // Show error notification
      setNotification({
        isVisible: true,
        type: 'error',
        title: 'Application Failed',
        message: data.error || 'Failed to submit application. Please try again.'
      });
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  const getEmploymentTypeBadge = (type: string) => {
    const styles = {
      'Full-time': 'bg-green-100 text-green-800',
      'Part-time': 'bg-blue-100 text-blue-800',
      'Intern': 'bg-purple-100 text-purple-800',
    };
    return styles[type as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen gradient-bg">
      <Helmet>
        <title>{`${job.title} – Careers at Cura`}</title>
        <meta name="description" content={`Apply for the ${job.title} position at Cura. ${job.description.substring(0, 120)}...`} />
        <link rel="canonical" href={`https://mycura.org/careers/job/${job.id}`} />
        <meta property="og:title" content={`${job.title} – Careers at Cura`} />
        <meta property="og:description" content={`Apply for the ${job.title} position at Cura. ${job.description.substring(0, 120)}`} />
        <meta property="og:url" content={`https://mycura.org/careers/job/${job.id}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${job.title} – Careers at Cura`} />
        <meta name="twitter:description" content={`Apply for the ${job.title} position at Cura. ${job.description.substring(0, 120)}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": job.title,
            "description": job.description,
            "employmentType": job.employmentType === 'Full-time' ? 'FULL_TIME' : job.employmentType === 'Part-time' ? 'PART_TIME' : 'INTERN',
            "hiringOrganization": { "@type": "Organization", "name": "Cura", "sameAs": "https://mycura.org" },
            "jobLocation": { "@type": "Place", "address": { "@type": "PostalAddress", "addressCountry": "US" } }
          })}
        </script>
      </Helmet>
      {/* Back to careers button */}
      <div className="container mx-auto px-6 pt-8 pb-8">
        <button
          onClick={() => navigate('/careers')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Careers</span>
        </button>
      </div>

      <div className="container mx-auto px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-card p-8 mb-8"
          >
            <div className="flex flex-wrap items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">{job.title}</h1>
                <p className="text-gray-600">{job.category}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEmploymentTypeBadge(job.employmentType)}`}>
                {job.employmentType}
              </span>
            </div>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-xl font-semibold text-gray-800 mb-3">Overview</h2>
              <p className="text-gray-700 mb-6">{job.overview}</p>

              <h2 className="text-xl font-semibold text-gray-800 mb-3">Key Responsibilities</h2>
              <ul className="list-disc list-inside space-y-2 mb-6">
                {job.responsibilities.map((resp, index) => (
                  <li key={index} className="text-gray-700">{resp}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-800 mb-3">Qualifications</h2>
              <ul className="list-disc list-inside space-y-2 mb-6">
                {job.qualifications.map((qual, index) => (
                  <li key={index} className="text-gray-700">{qual}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-800 mb-3">Benefits</h2>
              <ul className="list-disc list-inside space-y-2">
                {job.benefits.map((benefit, index) => (
                  <li key={index} className="text-gray-700">{benefit}</li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Application Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ApplicationForm job={job} onSubmit={handleApplicationSubmit} />
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Notification */}
      <Notification
        isVisible={notification.isVisible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
        autoClose={notification.type === 'success'}
        duration={notification.type === 'success' ? 3000 : 8000}
      />
    </div>
  );
};
